package com.example.readingnotes.data.remote.api

import com.example.readingnotes.data.remote.dto.*
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.parameter
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody

expect fun createHttpClient(baseUrl: String): HttpClient

class ApiClient(
    private val baseUrl: String,
) {
    private val client = createHttpClient(baseUrl)

    val httpClient = client

    // Auth
    suspend fun login(password: String): LoginResponseDto =
        client
            .post(ApiRoutes.AUTH_LOGIN) {
                setBody(LoginRequestDto(password))
            }.body()

    suspend fun logout(): LogoutResponseDto = client.post(ApiRoutes.AUTH_LOGOUT).body()

    suspend fun getAuthState(): AuthStateResponseDto = client.get(ApiRoutes.AUTH_ME).body()

    // Posts
    suspend fun getPosts(page: Int = 1, pageSize: Int = 20): List<PostDto> =
        client
            .get(ApiRoutes.POSTS) {
                parameter("page", page)
                parameter("pageSize", pageSize)
            }.body()

    suspend fun getPost(id: Int): PostDto = client.get(ApiRoutes.post(id)).body()

    suspend fun createPost(request: CreatePostRequestDto): PostDto =
        client
            .post(ApiRoutes.POSTS) {
                setBody(request)
            }.body()

    // Comments
    suspend fun getComments(postId: Int): List<CommentDto> = client.get(ApiRoutes.comments(postId)).body()

    suspend fun createComment(postId: Int, request: CreateCommentRequestDto): CommentDto =
        client
            .post(ApiRoutes.comments(postId)) {
                setBody(request)
            }.body()

    // New Auth methods
    suspend fun register(request: RegisterRequestDto): UserDto =
        client
            .post(ApiRoutes.AUTH_REGISTER) {
                setBody(request)
            }.body()

    suspend fun loginWithUsername(request: UsernameLoginRequestDto): UsernameLoginResponseDto =
        client
            .post(ApiRoutes.AUTH_LOGIN_USERNAME) {
                setBody(request)
            }.body()

    suspend fun verifyTotp(request: TotpVerifyRequestDto): TotpVerifyResponseDto =
        client
            .post(ApiRoutes.AUTH_VERIFY_TOTP) {
                setBody(request)
            }.body()

    suspend fun setupTotp(): TotpSetupResponseDto = client.post(ApiRoutes.AUTH_SETUP_TOTP).body()

    suspend fun updateUsername(request: UpdateUsernameRequestDto): UserDto =
        client
            .put(ApiRoutes.AUTH_USERNAME) {
                setBody(request)
            }.body()

    suspend fun checkUsernameAvailable(username: String): UsernameAvailableResponseDto =
        client
            .get(
                ApiRoutes.checkUsername(username),
            ).body()

    // Email authentication methods
    suspend fun sendVerificationCode(request: SendVerificationCodeRequestDto): SendVerificationCodeResponseDto =
        client
            .post(ApiRoutes.AUTH_SEND_VERIFICATION_CODE) {
                setBody(request)
            }.body()

    suspend fun verifyEmail(request: VerifyEmailRequestDto): EmailAuthResponseDto =
        client
            .post(ApiRoutes.AUTH_VERIFY_EMAIL) {
                setBody(request)
            }.body()

    suspend fun loginWithCode(request: LoginWithCodeRequestDto): EmailAuthResponseDto =
        client
            .post(ApiRoutes.AUTH_LOGIN_WITH_CODE) {
                setBody(request)
            }.body()

    // New Post methods
    suspend fun updatePost(id: Int, request: UpdatePostRequestDto, totpCode: String? = null): PostDto =
        client
            .put(ApiRoutes.post(id)) {
                setBody(request)
                totpCode?.let { header("X-TOTP-Code", it) }
            }.body()

    suspend fun deletePost(id: Int, totpCode: String? = null): DeleteResponseDto =
        client
            .delete(ApiRoutes.post(id)) {
                totpCode?.let { header("X-TOTP-Code", it) }
            }.body()

    suspend fun updatePostStatus(id: Int, status: String, totpCode: String? = null): PostDto =
        client
            .patch(ApiRoutes.postStatus(id)) {
                setBody(UpdatePostStatusRequestDto(status))
                totpCode?.let { header("X-TOTP-Code", it) }
            }.body()

    // New Comment methods
    suspend fun deleteComment(id: Int, totpCode: String? = null): DeleteResponseDto =
        client
            .delete(ApiRoutes.comment(id)) {
                totpCode?.let { header("X-TOTP-Code", it) }
            }.body()

    suspend fun bulkDeleteComments(ids: List<Int>, totpCode: String? = null): BulkDeleteResponseDto =
        client
            .post(ApiRoutes.COMMENTS_BULK_DELETE) {
                setBody(BulkDeleteRequestDto(ids))
                totpCode?.let { header("X-TOTP-Code", it) }
            }.body()

    // Audit logs
    suspend fun getAuditLogs(
        page: Int = 1,
        pageSize: Int = 20,
        startDate: String? = null,
        endDate: String? = null,
        actionType: String? = null,
    ): PaginatedAuditLogsResponseDto =
        client
            .get(ApiRoutes.AUDIT_LOGS) {
                parameter("page", page)
                parameter("pageSize", pageSize)
                startDate?.let { parameter("startDate", it) }
                endDate?.let { parameter("endDate", it) }
                actionType?.let { parameter("actionType", it) }
            }.body()

    // Images
    suspend fun getImageUploadUrl(request: ImageUploadUrlRequestDto): ImageUploadUrlResponseDto =
        client
            .post(ApiRoutes.IMAGES_UPLOAD_URL) {
                setBody(request)
            }.body()

    suspend fun confirmImageUpload(request: ImageConfirmRequestDto): ImageDto =
        client
            .post(ApiRoutes.IMAGES_CONFIRM) {
                setBody(request)
            }.body()

    suspend fun getImage(id: Int): ImageDto = client.get(ApiRoutes.image(id)).body()

    suspend fun deleteImage(id: Int): DeleteResponseDto = client.delete(ApiRoutes.image(id)).body()

    // Tags
    suspend fun getTags(): List<TagDto> = client.get(ApiRoutes.TAGS).body()

    suspend fun getTag(id: Int): TagDto = client.get(ApiRoutes.tag(id)).body()

    suspend fun getPopularTags(limit: Int = 10): List<TagWithCountDto> =
        client
            .get(ApiRoutes.TAGS_POPULAR) {
                parameter("limit", limit)
            }.body()

    suspend fun getTagsForPost(postId: Int): List<TagDto> = client.get(ApiRoutes.tagsForPost(postId)).body()

    suspend fun createTag(request: CreateTagRequest): TagDto =
        client
            .post(ApiRoutes.TAGS) {
                setBody(request)
            }.body()

    suspend fun updateTag(id: Int, request: UpdateTagRequest): TagDto =
        client
            .put(ApiRoutes.tag(id)) {
                setBody(request)
            }.body()

    suspend fun deleteTag(id: Int): DeleteResponseDto = client.delete(ApiRoutes.tag(id)).body()

    suspend fun addTagToPost(postId: Int, tagId: Int): Unit =
        client.post(ApiRoutes.addTagToPost(postId, tagId)).body()

    suspend fun removeTagFromPost(postId: Int, tagId: Int): Unit =
        client.delete(ApiRoutes.addTagToPost(postId, tagId)).body()

    suspend fun searchTags(query: String): List<TagDto> =
        client
            .get(ApiRoutes.TAGS_SEARCH) {
                parameter("q", query)
            }.body()

    // Search
    suspend fun searchPosts(
        query: String,
        page: Int = 1,
        pageSize: Int = 20,
        status: String? = null,
    ): SearchResponseDto =
        client
            .get(ApiRoutes.SEARCH) {
                parameter("q", query)
                parameter("page", page)
                parameter("pageSize", pageSize)
                status?.let { parameter("status", it) }
            }.body()

    // Notifications
    suspend fun getNotificationSettings(): NotificationSettingsDto =
        client.get(ApiRoutes.PUSH_SETTINGS).body()

    suspend fun updateNotificationSettings(request: UpdateNotificationSettingsRequestDto): NotificationSettingsDto =
        client
            .put(ApiRoutes.PUSH_SETTINGS) {
                setBody(request)
            }.body()

    suspend fun getNotifications(
        limit: Int = 30,
        offset: Int = 0,
        unreadOnly: Boolean = false,
    ): NotificationsResponseDto =
        client
            .get(ApiRoutes.PUSH_NOTIFICATIONS) {
                parameter("limit", limit)
                parameter("offset", offset)
                parameter("unread", unreadOnly.toString())
            }.body()

    suspend fun markNotificationAsRead(id: Int): MarkAsReadResponseDto =
        client.patch(ApiRoutes.pushNotification(id)).body()

    suspend fun markAllNotificationsAsRead(): MarkAllAsReadResponseDto =
        client.post(ApiRoutes.PUSH_NOTIFICATIONS_READ_ALL).body()
}
